import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Power, Trash2, Plus } from 'lucide-react';
import { Card, Badge, Btn, Alert, Truncate, ConfirmDialog, QueryState } from './UI';
import { useToast } from './Toast';
import { useAuth } from '../contexts/AuthContext';
import { useStepUp } from '../contexts/StepUpContext';
import { companyAssetsApi, freshRequest, sendFresh } from '../services/api';

/* Asset-regiszter — egy asset-típus kapcsolatainak ŐSZINTE állapota (M5/M11,
 * wiki/proposals/asset-registry.md).
 *
 * ⚠ A Badge SOHA nem vált „Aktív"-ra a mentés 200-as válaszától — kizárólag a
 * `/test` által ELMENTETT eredménytől (`status`, `last_checked_at`, `last_check_ok`).
 * A `last_check_error` MÁR szanitizált a backenden (asset_probe.sanitize_test_error):
 * itt nincs saját csonkolás/maszkolás, csak a meglévő `Truncate` (natív tooltip).
 *
 * CO_WORKER vs ADMIN — ÚJ minta a kódbázisban (D2 UX §5): OLVASÁS mindenkinek, a
 * „Kapcsolat tesztelése" is (mellékhatás-mentes); az ÍRÓ műveletek (letiltás,
 * engedélyezés, törlés) `disabled` + `title` tooltip CO_WORKER-nek — NEM elrejtve.
 * A törlés step-up-os (a backend dekorátora + itt előzetes `requireFreshStepUp` — 2026-09-24 óta
 * friss, erre a kérésre kötött passkey, nem a 30 perces jegy).
 *
 * Ez az alapréteg: a típusonkénti létrehozó/credential-felületek (Billingo, bankszámla
 * onboarding, NAV OSA) az M6–M10 munkacsomagok — addig ez a blokk a meglévő
 * kapcsolatokat mutatja, és a `emptyText`-et, ha nincs egy sem. */

const STATUS_BADGE = {
  active:   { variant: 'success', label: 'Aktív' },
  pending:  { variant: 'warning', label: 'Teszt szükséges' },
  error:    { variant: 'danger',  label: 'Hibás kapcsolat' },
  disabled: { variant: 'default', label: 'Letiltva' },
};

const fmtTs = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${p(d.getMonth()+1)}.${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/* `actions`: a Card fejlécébe (pl. „Billingo-fiók hozzáadása" gomb — a típus saját
 * felülete adja, M7). `renderExtra(asset)`: típus-specifikus tartalom a sor aljára
 * (pl. a credential-állapot + „Csere"); az alapréteg nem tud a mezőkről.
 *
 * `registerProvider` (M6/M9/M10, singleton providerek: nav_upo, ms_graph, nav): ha a
 * cégnek MÉG NINCS aktív assete ezzel a providerrel, a fejlécben „Kapcsolat felvétele a
 * regiszterbe" gomb jelenik meg. Credential NÉLKÜL hozza létre a sort (a hitelesítő
 * adatokat a típus MEGLÉVŐ panelje / a seed kezeli, azokat nem érinti) → `pending`, és
 * a „Kapcsolat tesztelése" adja az őszinte állapotot. Admin + step-up (a `POST
 * /company-assets` dekorátora kéri, itt ELŐRE kérdezünk). */
export default function AssetStatusBlock({ assetType, title, subtitle, emptyText, actions, renderExtra,
                                           registerProvider, registerLabel, registerProviders }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { isAdmin } = useAuth();
  const { requireFreshStepUp } = useStepUp();
  const [confirm, setConfirm] = useState(null);
  const q = useQuery({
    queryKey: ['company-assets', assetType],
    queryFn: () => companyAssetsApi.list(assetType),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['company-assets', assetType] });
  const onErr = (e) => toast.error(e?.response?.data?.detail || e.message || 'A művelet sikertelen.');

  // 2026-09-24: felvétel és törlés friss, erre a kérésre kötött passkeyjel — a mutáció a
  // `freshRequest` leírót küldi, ugyanazt, amire a passkey szól.
  const registerReq = ({ provider, label }) => freshRequest('POST /company-assets',
    { body: { asset_type: assetType, provider, label: label || null, credentials: {} } });
  const deleteReq = (id) => freshRequest('DELETE /company-assets/{asset_id}',
    { path: { asset_id: id }, query: { with_credentials: false } });
  const registerMut = useMutation({
    mutationFn: (req) => sendFresh(req),
    onSuccess: () => { toast.success('Kapcsolat felvéve a regiszterbe — élő teszt kell az aktiváláshoz.'); invalidate(); },
    onError: onErr,
  });
  /* Egy vagy több felvehető provider (`registerProvider` + `registerLabel`, vagy
   * `registerProviders=[{provider,label}]`); csak az jelenik meg, amelyikből még nincs aktív. */
  const candidates = registerProviders
    || (registerProvider ? [{ provider: registerProvider, label: registerLabel }] : []);
  const registerBtns = q.data ? candidates
    .filter(c => !(q.data.assets || []).some(a => a.provider === c.provider && !a.disabled_at))
    .map(c => (
      <Btn key={c.provider} size="sm" icon={Plus} disabled={!isAdmin || registerMut.isPending}
        loading={registerMut.isPending}
        title={isAdmin ? undefined : 'Csak cég-adminisztrátor módosíthatja.'}
        onClick={async () => {
          const req = registerReq(c);
          if (!await requireFreshStepUp('A kapcsolat regiszterbe vétele előtt ', [req])) return;
          registerMut.mutate(req);
        }}>{candidates.length > 1 ? `${c.label || c.provider} felvétele` : 'Kapcsolat felvétele a regiszterbe'}</Btn>
    )) : [];
  const headerActions = (actions || registerBtns.length) ? <>{actions}{registerBtns}</> : undefined;

  const testMut = useMutation({
    mutationFn: (id) => companyAssetsApi.test(id),
    onSuccess: (a) => {
      invalidate();
      if (a.last_check_ok) toast.success('A kapcsolat működik.');
      else toast.error('A kapcsolat-teszt sikertelen — a részletek a kártyán.');
    },
    onError: onErr,
  });
  const disableMut = useMutation({ mutationFn: (id) => companyAssetsApi.disable(id),
    onSuccess: () => { toast.success('Kapcsolat letiltva.'); invalidate(); }, onError: onErr });
  const enableMut = useMutation({ mutationFn: (id) => companyAssetsApi.enable(id),
    onSuccess: () => { toast.success('Kapcsolat engedélyezve — élő teszt kell az aktiváláshoz.'); invalidate(); },
    onError: onErr });
  const deleteMut = useMutation({ mutationFn: (req) => sendFresh(req),
    onSuccess: () => { toast.success('Kapcsolat törölve.'); invalidate(); }, onError: onErr });

  const adminTitle = isAdmin ? undefined : 'Csak cég-adminisztrátor módosíthatja.';
  const assets = q.data?.assets || [];

  return (
    <Card title={title} subtitle={subtitle} actions={headerActions}>
      <QueryState query={q} empty={assets.length === 0}
        emptyText={emptyText || 'Ehhez a típushoz még nincs bekötött kapcsolat.'}>
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {assets.map(a => {
            const sb = STATUS_BADGE[a.status] || STATUS_BADGE.pending;
            const busy = testMut.isPending || disableMut.isPending || enableMut.isPending || deleteMut.isPending;
            return (
              <div key={a.id} style={{border:'1px solid var(--border)',borderRadius:'var(--radius)',
                padding:'0.6rem 0.8rem',display:'flex',flexDirection:'column',gap:6}}>
                <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                  <Truncate title={a.label || a.provider_label} style={{fontWeight:600,fontSize:'0.88rem',maxWidth:260}}>
                    {a.label || a.provider_label}
                  </Truncate>
                  <span style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>{a.provider_label}</span>
                  <Badge variant={sb.variant}>{sb.label}</Badge>
                  {a.credential_less && a.status === 'active' && (
                    <span style={{fontSize:'0.72rem',color:'var(--text-dim)'}}>
                      aktív · nincs élő teszt (kivonat-feltöltéssel aktiválva)
                    </span>
                  )}
                  <span style={{marginLeft:'auto',fontSize:'0.72rem',color:'var(--text-muted)'}}>
                    {a.last_checked_at ? `Utolsó teszt: ${fmtTs(a.last_checked_at)}` : 'Még nem volt tesztelve'}
                  </span>
                </div>
                {a.status === 'error' && a.last_check_error && (
                  <Alert type="danger" style={{marginBottom:0,padding:'0.45rem 0.7rem'}}>
                    <Truncate title={a.last_check_error}>{a.last_check_error}</Truncate>
                  </Alert>
                )}
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {a.has_probe && !a.credential_less && !a.disabled_at && (
                    <Btn size="sm" variant="secondary" icon={RefreshCw} loading={testMut.isPending}
                      onClick={()=>testMut.mutate(a.id)}>Kapcsolat tesztelése</Btn>
                  )}
                  {a.disabled_at ? (
                    <Btn size="sm" variant="ghost" icon={Power} disabled={!isAdmin || busy} title={adminTitle}
                      onClick={()=>enableMut.mutate(a.id)}>Engedélyezés</Btn>
                  ) : (
                    <Btn size="sm" variant="ghost" icon={Power} disabled={!isAdmin || busy} title={adminTitle}
                      onClick={()=>disableMut.mutate(a.id)}>Letiltás</Btn>
                  )}
                  <Btn size="sm" variant="ghost" icon={Trash2} disabled={!isAdmin || busy} title={adminTitle}
                    onClick={()=>setConfirm(a)}>Törlés</Btn>
                </div>
                {renderExtra ? renderExtra(a) : null}
              </div>
            );
          })}
        </div>
      </QueryState>
      <ConfirmDialog open={!!confirm} danger title="Kapcsolat törlése"
        message={<>A(z) <b>{confirm?.label || confirm?.provider_label}</b> kapcsolat törlődik a regiszterből.
          A hitelesítő adatok NEM törlődnek ezzel (azt a típus saját felülete kezeli). Friss megerősítést kérünk.</>}
        confirmLabel="Törlés" loading={deleteMut.isPending}
        onCancel={()=>setConfirm(null)}
        onConfirm={async ()=>{
          const a = confirm; setConfirm(null);
          const req = deleteReq(a.id);
          if (!await requireFreshStepUp('A kapcsolat törlése előtt ', [req])) return;
          deleteMut.mutate(req);
        }}/>
    </Card>
  );
}
